/* eslint-disable class-methods-use-this */
import { IChartAreaRangeData, IChartData } from '../ChartDefinitions';

class MarketIndicatorChartDateService {
    toMonthlyData(data: IChartData[] | IChartAreaRangeData[], minimumDate?: Date, maximumDate?: Date): IChartData[] | IChartAreaRangeData[] {
        minimumDate?.setHours(0, 0, 0, 0);

        const filteredData = data
            .filter((x) => {
                if (!maximumDate) {
                    return x;
                }

                x.asOfDate.setHours(0, 0, 0, 0);

                return x.asOfDate.getTime() <= maximumDate.getTime();
            })
            .filter((x) => {
                if (!minimumDate) {
                    return x;
                }

                return x.asOfDate.getTime() >= minimumDate.getTime();
            });

        return filteredData;
    }

    toWeeklyData(data: IChartData[] | IChartAreaRangeData[], minimumDate?: Date, maximumDate?: Date): IChartData[] | IChartAreaRangeData[] {
        const weeks: { [weekDate: string]: IChartData[] } = {};

        const filteredData = data
            .filter((x) => {
                if (!maximumDate) {
                    return x;
                }

                return this.getWeekStartDate(x.asOfDate).getTime() <= maximumDate.getTime();
            })
            .filter((x) => {
                if (!minimumDate) {
                    return x;
                }

                return this.getWeekStartDate(x.asOfDate).getTime() >= minimumDate.getTime();
            });

        filteredData.forEach((day) => {
            const weekStartDate = this.getWeekStartDate(day.asOfDate).toISOString().split('T')[0];
            if (!weeks[weekStartDate]) {
                weeks[weekStartDate] = [];
            }
            weeks[weekStartDate].push(day);
        });

        const sortedWeeksKeys = Object.keys(weeks).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        const weeklyData: IChartData[] | IChartAreaRangeData[] = sortedWeeksKeys.map((weekDate, index, weekDates) => {
            const weekData = weeks[weekDate];
            const averageValue = weekData.reduce((sum, day) => sum + day.value, 0) / weekData.length;
            const isLastWeekDate = index === weekDates.length - 1;
            let newestAsOfDate = weekDate;

            if (isLastWeekDate && maximumDate) {
                const lastWeekDateMaximumDate = new Date(Math.max(...weekData.map((day) => new Date(day.asOfDate).getTime())));
                const previousWeekFromMaxDate = new Date(this.getPreviousWeek(maximumDate)?.setHours(0, 0, 0, 0));
                newestAsOfDate = !(lastWeekDateMaximumDate >= previousWeekFromMaxDate) ? lastWeekDateMaximumDate.toDateString() : weekDate;
            }

            if (data.some((x) => (x as IChartAreaRangeData).maximumValue || (x as IChartAreaRangeData).minimumValue)) {
                const maximumValue = Math.max(...(weekData as IChartAreaRangeData[]).map((x) => x.maximumValue));
                const minimumValue = Math.min(...(weekData as IChartAreaRangeData[]).map((x) => x.minimumValue));

                return {
                    value: averageValue,
                    minimumValue,
                    maximumValue,
                    asOfDate: new Date(newestAsOfDate),
                    isActualValue: true,
                };
            }

            return { value: averageValue, asOfDate: new Date(newestAsOfDate), isActualValue: true };
        });

        return weeklyData;
    }

    toWeeklySeriesData(
        data: IChartData[] | IChartAreaRangeData[],
        minimumChartDisplayDate: Date,
        isThisYearLine: boolean = false,
        isAverageCount: boolean = false,
    ): IChartData[] | IChartAreaRangeData[] {
        if (!data.length) {
            return [];
        }
        const firstItemYear = (data[0].originalAsOfDate || data[0].asOfDate).getFullYear();

        const seriesData = data.map((item, index, array) => {
            let newOriginalAsOfDate = this.getWeekStartDate(item.originalAsOfDate ?? item.asOfDate, 0, isThisYearLine);
            let newAsOfDate = this.getWeekStartDate(item.asOfDate, 0);

            if (isThisYearLine && index === array.length - 1 && !isAverageCount) {
                newOriginalAsOfDate = item.originalAsOfDate ?? item.asOfDate;
            }

            if (index === 0 && this.getWeekStartDate(newOriginalAsOfDate, 0).getTime() < minimumChartDisplayDate.getTime()) {
                newOriginalAsOfDate = new Date(minimumChartDisplayDate);
                newOriginalAsOfDate.setFullYear(firstItemYear);
            }

            if (index === 0 && this.getWeekStartDate(newAsOfDate, 0).getTime() < minimumChartDisplayDate.getTime()) {
                newAsOfDate = new Date(minimumChartDisplayDate);
                newAsOfDate.setFullYear(item.asOfDate.getFullYear());
            }

            return {
                ...item,
                asOfDate: newAsOfDate,
                originalAsOfDate: newOriginalAsOfDate,
            };
        });

        return seriesData;
    }

    getWeekStartDate(date: Date, dayOffset: number = 1, isThisYearAverageCount?: boolean): Date {
        const diff = date.getUTCDate() - date.getUTCDay() + dayOffset;
        const weekStartDate = new Date(date);
        weekStartDate.setUTCDate(diff);

        if (isThisYearAverageCount && weekStartDate.getHours()) {
            weekStartDate.setHours(weekStartDate.getHours() + 12);
            weekStartDate.setHours(0, 0, 0, 0);
        }

        return weekStartDate;
    }

    getPreviousYearsWeeks = (date: Date, yearsBack: number): Date[] => {
        const previousYearsWeeks: Date[] = [];
        for (let i = 1; i <= yearsBack; i += 1) {
            const previousYearsWeek = this.getPreviousYearWeek(date, i);
            previousYearsWeeks.push(previousYearsWeek);
        }
        return previousYearsWeeks;
    };

    getPreviousYearsMonths = (date: Date, yearsBack: number): Date[] => {
        const previousYearsMonths: Date[] = [];
        for (let i = 1; i <= yearsBack; i += 1) {
            const previousYearsMonth = this.getPreviousYearMonth(date, i);
            previousYearsMonths.push(previousYearsMonth);
        }
        return previousYearsMonths;
    };

    getPreviousYearWeek = (date: Date, yearsBack: number = 1): Date => {
        const previousYearDate = new Date(date);
        previousYearDate.setUTCFullYear(date.getUTCFullYear() - yearsBack);
        const previousYearsWeek = this.getWeekStartDate(previousYearDate);
        return previousYearsWeek;
    };

    getPreviousYearMonth = (date: Date, yearsBack: number = 1): Date => {
        const previousYearDate = new Date(date);
        previousYearDate.setUTCFullYear(date.getUTCFullYear() - yearsBack);
        previousYearDate.setDate(1);
        return previousYearDate;
    };

    getPreviousWeeks = (date: Date, weeksBack: number): Date[] => {
        const previousWeeks: Date[] = [];
        for (let i = 1; i <= weeksBack; i += 1) {
            const previousWeek = this.getPreviousWeek(date, i);
            previousWeeks.push(previousWeek);
        }
        return previousWeeks;
    };

    getPreviousWeek = (date: Date, weeksBack: number = 1): Date => {
        const previousWeekDate = new Date(date);
        previousWeekDate.setDate(date.getDate() - date.getDay() - 7 * weeksBack);
        const previousWeek = this.getWeekStartDate(previousWeekDate);
        return previousWeek;
    };
}

const marketIndicatorChartDateService = new MarketIndicatorChartDateService();
export default marketIndicatorChartDateService;
